/**
 * components/icons/XgIcon.tsx —— **业务/文件类型图标**统一集（U-12⑤；U-S3 缺件建件）
 * 真源（102，逐字读）：`components/icons/XgIcon.vue`（97 行）。
 * 依据：设计件 §9.5.5「全库 emoji 图标退役；引入 16px stroke 图标集（outline 风格、currentColor、
 *       1.5 stroke-width，与现有设置/折叠 SVG 同风格）；**禁引入运行时图标库依赖**（静态内联 SVG）」。
 * 形制：viewBox `0 0 16 16`，stroke=currentColor，stroke-width=1.5，圆头圆角；容器默认 16×16。
 * 用法：`<XgIcon name="paperclip" />` ／ `<XgIcon name="file" size={14} />`
 *
 * 纪律：零取数（props 注入）；不新增依赖；未登记名 ⇒ 回落 `file`（不崩、不空白）；
 *       **图标名键集＝102 逐字**（11 名），缺的名不臆造。
 */
import React from "react";

/** 图标名闭集（逐字＝102 `PATHS` 键；装配层按文件类型选名） */
export const XG_ICON_NAMES = [
  "file",
  "image",
  "sheet",
  "archive",
  "audio",
  "video",
  "pdf",
  "doc",
  "ppt",
  "paperclip",
  "mic",
] as const;
export type XgIconName = (typeof XG_ICON_NAMES)[number];

/** 图标路径表（name → path 数组；全部按 16×16 网格自绘，禁第三方库） */
export const XG_ICON_PATHS: Readonly<Record<XgIconName, readonly string[]>> = Object.freeze({
  file: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M9.5 2v3h3",
  ],
  image: [
    "M3.5 4.5h9v7h-9z",
    "M6 8.3a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    "M4 11l3-3 2.5 2.5L11 8l1.5 1.5",
  ],
  sheet: [
    "M3.5 4.5h9v7h-9z",
    "M3.5 7.5h9",
    "M6.5 4.5v7",
  ],
  archive: [
    "M3.5 3.5h9v2h-9z",
    "M4.2 5.5h7.6v6.5H4.2z",
    "M7 8h2",
  ],
  audio: [
    "M6.5 11V4.8l4.5-1V10",
    "M6.5 11a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0Z",
    "M11 10a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0Z",
  ],
  video: [
    "M3.5 5.5h9v6h-9z",
    "M6.8 7.4l2.8 1.6-2.8 1.6z",
  ],
  pdf: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.2 9.6h3.6",
  ],
  doc: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.2 8.2h3.6",
    "M6.2 10.4h2.6",
  ],
  ppt: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.4 10.8V7.4h1.8a1.4 1.4 0 0 1 0 2.8H6.4",
  ],
  paperclip: [
    "M10.8 5.6 7.1 9.3a1.5 1.5 0 1 0 2.1 2.1l3.9-3.9a3 3 0 0 0-4.2-4.2l-3.9 3.9a4.5 4.5 0 0 0 6.4 6.4l3.2-3.2",
  ],
  mic: [
    "M8 2.4a1.5 1.5 0 0 1 1.5 1.5v3.4a1.5 1.5 0 0 1-3 0V3.9A1.5 1.5 0 0 1 8 2.4Z",
    "M4.6 7.4a3.4 3.4 0 0 0 6.8 0",
    "M8 10.8v2.4",
  ],
});

/** 未登记名回落（102：`PATHS[props.name] || PATHS.file`） */
export const XG_ICON_FALLBACK: XgIconName = "file";

/** 查表：名 → path 数组；未登记名 ⇒ 回落 `file`（不臆造、不抛错） */
export function xgIconPaths(name: string): readonly string[] {
  if (typeof name === "string" && Object.prototype.hasOwnProperty.call(XG_ICON_PATHS, name)) {
    return (XG_ICON_PATHS as Readonly<Record<string, readonly string[]>>)[name];
  }
  return XG_ICON_PATHS[XG_ICON_FALLBACK];
}

export interface XgIconProps {
  name: string;
  /** 102 `size: [Number, String], default: 16`（逐字） */
  size?: number | string;
}

const CSS = `
.xg-icon {
  display: inline-block;
  vertical-align: -0.15em;
  flex: none;
}
`;
import { cssIds } from "../../styles/cssIds";
if (typeof document !== "undefined" && !document.getElementById(cssIds.xgIcon)) {
  const el = document.createElement("style");
  el.id = cssIds.xgIcon;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** 业务/文件类型图标（静态内联 SVG；viewBox 恒 16×16） */
export function XgIcon({ name, size = 16 }: XgIconProps): React.ReactElement {
  const d = xgIconPaths(name);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="xg-icon"
      data-xg-icon={name}
    >
      {d.map((p, i) => (
        <path
          key={i}
          d={p}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
